if (!customElements.get('product-sample-button')) {
  class SampleButton extends HTMLElement {
    constructor() {
      super();

      this.maxSamples = window.sampleStrings.max || 3;
      this.variantId = this.dataset.variantId;
      this.title = this.dataset.title;
      this.product_type = this.dataset.productType;
      this.delivery_time = this.dataset.deliveryTime;
      this.errorMessage = window.sampleStrings.error;
      this.cartNotification = document.querySelector('cart-notification');
      this.cartDrawer = document.querySelector('cart-drawer');

      this.addEventListener('click', (event) => {
        event.preventDefault();
        this.addSample();
      });
    }

    /**
     * Add sample to cart
     */
    async addSample() {
      if (!this.variantId) {
        return;
      }

      // Set in processing state
      this.toggleLoading(true);

      // Get current card data
      const cartData = await this.getCartData();

      // Check if there are max 3 samples is already in cart
      const sampleItems = cartData.items.filter(
        (item) => item.properties._sample && item.properties._product_type === this.product_type
      );
      if (sampleItems.length >= this.maxSamples) {
        this.showNotification({
          errors: this.errorMessage.replace('{{ total }}', this.maxSamples),
        });
        this.toggleLoading(false);
        return;
      }

      // Add sample to cart
      try {
        // Add to cart body
        const body = JSON.stringify({
          items: [
            {
              id: parseInt(this.variantId),
              quantity: 1,
              properties: {
                _ref: new Date().getTime(),
                _sample: true,
                _token: this.generateToken(0, this.variantId),
                _title: `${window.sampleStrings.prefix}${this.title}`,
                ...(this.product_type && {
                  _product_type: this.product_type,
                }),
                ...(this.delivery_time && {
                  _delivery_time: this.delivery_time,
                }),
              },
            },
          ],
          sections: this.getSectionsToRender(),
          sections_url: window.location.pathname,
        });

        // Add to cart request
        const addToCartResponse = await fetch(`${routes.cart_add_url}`, {
          ...fetchConfig('json', 'POST'),
          ...{ body },
        });
        const addToCartData = await addToCartResponse.json();

        // Get product data
        if (!addToCartData.items) {
          throw new Error('No items added to the cart');
        }

        // Get product data
        const productData = addToCartData.items[0];

        // Show notification
        this.showNotification(addToCartData);

        // End Processing state
        this.toggleLoading(false);

        // Push to Shopify analytics pixel
        if (Shopify?.analytics) {
          const dataLayer = window.dataLayer || [];

          // Push to dataLayer
          dataLayer.push({
            event: 'sample_added_to_cart',
            price: productData.price,
            currency: Shopify?.currency?.active || 'EUR',
            product_title: productData.properties._title,
            product_type: productData.properties._product_type,
            quantity: productData.quantity,
            total_cost: productData.total_price,
          });

          // Push to Shopify analytics
          Shopify.analytics.publish('sample_added_to_cart', {
            price: productData.price,
            currency: Shopify?.currency?.active || 'EUR',
            product_title: productData.properties._title,
            product_type: productData.properties._product_type,
            quantity: productData.quantity,
            total_cost: productData.total_price,
          });
        }
      } catch (error) {
        console.error(error);
        this.toggleLoading(false);
      }
    }

    /**
     * Generate the token based on the price and variant id
     * @info
     * This logic is here to camouflage the end price per unit since it's calculated in the frontend and send to the function in the backend with a line item property.
     * The ouput is a mix of the price an the variant id
     * Character 1 says how many characters are the euros (nth)
     * Character 5 and 6 are the cents (if cents are lower then 10, add a 0 in front)
     * Character 11 till (11 + nth) are the euros
     * The remaining characters are the variant id
     * @param {int} price
     * @returns {string} - token
     * @example getPriceFromToken('34709506153333146660') // 333.95
     * 3 470 95 0615 333 3146660 (without the spaces)
     * (Character 1 is 3, Character 5 and 6 are 95, Character 11 till (11 + 3) are 333)
     * @param {number} price
     * @param {number} variantId
     * @returns {string} token
     */
    generateToken(price, variantId) {
      // Get the euros, cents and the length of the euros separately
      let euros = Math.floor(price / 100);
      let cents = price % 100;
      let euroLength = euros.toString().length;
      if (cents <= 9) {
        cents = `0${cents}`;
      }

      // Split the variant id in 3 parts
      const variantPart1 = variantId.toString().substring(0, 3);
      const variantPart2 = variantId.toString().substring(3, 7);
      const variantPart3 = variantId.toString().substring(7);

      // Euro length + variantPart1 + cents + variantPart2 + euros + variantPart3
      const token = `${euroLength}${variantPart1}${cents}${variantPart2}${euros}${variantPart3}`;

      return token;
    }

    /**
     * Toggle loading state of submit button
     * @param {*} active: boolean
     */
    toggleLoading(active = true) {
      this.toggleAttribute('disabled', active);
      this.classList.toggle('button--loading', active);
    }

    /**
     * @returns current cart data
     */
    async getCartData() {
      const cartResponse = await fetch(`${Shopify.routes.root}cart.js`, {
        ...fetchConfig('json', 'GET'),
      });
      const cartData = await cartResponse.json();
      return cartData;
    }

    /**
     * Show add to cart notification to user
     * @param {object} state : add to cart response
     * @returns
     */
    showNotification(state) {
      if (!state) return;

      // Show error message if something went wrong
      if (state.errors) {
        this.cartNotification.setActiveElement(document.activeElement);
        this.cartNotification.renderError(state.errors);
        return;
      }

      // Update cart drawer data
      this.cartDrawer && this.cartDrawer.renderContents(state);

      // Show notification as drawer or notification
      switch (this.dataset.addToCartBehavior) {
        case 'open_cart_drawer':
          // Set timeout to force animation. Because content is just updated with renderContents
          setTimeout(() => {
            this.cartDrawer && this.cartDrawer.open();
          });
          break;
        case 'show_cart_notification':
          this.cartNotification.setActiveElement(document.activeElement);
          this.cartNotification.renderContents(state);
      }
    }

    /**
     * @returns Sections to render after add to catt action
     */
    getSectionsToRender() {
      let sections = [...this.cartNotification.getSectionsToRender().map((section) => section.id)];
      if (this.cartDrawer) {
        sections = [
          ...sections,
          ...this.cartDrawer.getSectionsToRender().map((section) => section.id),
        ];
      }
      return sections;
    }
  }

  window.SampleButton = SampleButton;

  customElements.define('sample-button', SampleButton);
}
